import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Separator } from "@/components/ui/separator";
import { SidebarBrand, SwitchAppList, hasIntensiveAccess } from "@/components/AppSidebar";
import { 
  Home, 
  FileText, 
  BookOpen, 
  MessageSquare, 
  Mail,
  LogOut,
  Menu,
  User,
  GraduationCap,
  Shield,
  Users,
  Video,
  History,
  Bell,
  Settings,
  Smartphone,
  ChevronLeft,
  PanelLeftClose,
  PanelLeft,
  Link2,
  Lock,
  Route as RouteIcon,
} from "lucide-react";
import { toast } from "react-hot-toast";

// Account-level pages that must stay reachable for every logged-in user regardless of
// Discipleship Intensive access (profile info, app version history) — not Intensive content.
const ALWAYS_ACCESSIBLE_PATHS = ['/profile', '/versions'];

const Layout = ({ user, children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationCount, setNotificationCount] = useState(0);
  const seenMessageIds = useRef(new Set());
  const isInitialLoad = useRef(true);
  const canAccess = hasIntensiveAccess(user);
  const canRenderPage = canAccess || ALWAYS_ACCESSIBLE_PATHS.includes(location.pathname);

  useEffect(() => {
    if (!canAccess) return;
    const showMessageNotification = (message, isGroup = false) => {
      const senderName = message.sender_name || "Someone";
      const preview = message.content 
        ? message.content.substring(0, 50) + (message.content.length > 50 ? "..." : "")
        : "Sent an attachment";
      const title = isGroup 
        ? `New message in ${message.group_name || 'Group'}`
        : `New message from ${senderName}`;
      const description = isGroup 
        ? `${senderName}: ${preview}`
        : preview;

      toast.custom((t) => (
        <div
          className={`${
            t.visible ? 'animate-enter' : 'animate-leave'
          } max-w-md w-full bg-white shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5`}
        >
          <div className="flex-1 w-0 p-4">
            <div className="flex items-start">
              <div className="ml-3 flex-1">
                <p className="text-sm font-medium text-gray-900">{title}</p>
                <p className="mt-1 text-sm text-gray-500">{description}</p>
              </div>
            </div>
          </div>
          <div className="flex border-l border-gray-200">
            <button
              onClick={() => {
                toast.dismiss(t.id);
                navigate(isGroup ? '/messages' : `/messages/${message.sender_id}`);
              }}
              className="w-full border border-transparent rounded-none p-4 flex items-center justify-center text-sm font-medium text-blue-600 hover:text-blue-500 focus:outline-none"
            >
              View
            </button>
            <button
              onClick={() => toast.dismiss(t.id)}
              className="w-full border border-transparent rounded-none rounded-r-lg p-4 flex items-center justify-center text-sm font-medium text-gray-600 hover:text-gray-500 focus:outline-none border-l border-gray-200"
            >
              Close
            </button>
          </div>
        </div>
      ), { duration: 8000 });
    };

    const fetchUnreadMessages = async () => {
      try {
        const response = await axios.get(`${API}/messages/unread-all`);
        const directMessages = response.data.direct_messages || [];
        const groupMessages = response.data.group_messages || [];
        const countResponse = await axios.get(`${API}/messages/unread-count`);
        const directCount = countResponse.data.unread_count || 0;
        let groupCount = 0;
        try {
          const groupUnreadRes = await axios.get(`${API}/groups/unread`);
          groupCount = Object.values(groupUnreadRes.data || {}).reduce((sum, count) => sum + count, 0);
        } catch (e) { /* ignore */ }
        setUnreadCount(directCount + groupCount);

        if (isInitialLoad.current) {
          directMessages.forEach(msg => seenMessageIds.current.add(msg.message_id));
          groupMessages.forEach(msg => seenMessageIds.current.add(msg.message_id));
          isInitialLoad.current = false;
          return;
        }
        directMessages.forEach(msg => {
          if (!seenMessageIds.current.has(msg.message_id)) {
            seenMessageIds.current.add(msg.message_id);
            showMessageNotification(msg, false);
          }
        });
        groupMessages.forEach(msg => {
          if (!seenMessageIds.current.has(msg.message_id)) {
            seenMessageIds.current.add(msg.message_id);
            showMessageNotification(msg, true);
          }
        });
      } catch (error) {
        console.error("Error fetching unread messages:", error);
      }
    };

    const fetchNotificationCount = async () => {
      try {
        const response = await axios.get(`${API}/notifications/center?limit=100`);
        const unreadNotifs = (response.data || []).filter(n => !n.is_read);
        setNotificationCount(unreadNotifs.length);
      } catch (error) {
        console.error("Error fetching notification count:", error);
      }
    };

    fetchUnreadMessages();
    fetchNotificationCount();
    const interval = setInterval(() => {
      fetchUnreadMessages();
      fetchNotificationCount();
    }, 10000);
    return () => clearInterval(interval);
  }, [navigate, canAccess]);

  const navItems = [
    { 
      label: 'Dashboard', 
      icon: Home, 
      path: user?.role === 'teacher' ? '/teacher' : '/student',
      active: ['/teacher', '/student'].includes(location.pathname)
    },
    {
      label: user?.role === 'teacher' ? 'Students' : 'Classmates',
      icon: Users,
      path: '/students',
      active: location.pathname.startsWith('/students')
    },
    { 
      label: 'Assignments', 
      icon: FileText, 
      path: '/assignments',
      active: location.pathname.startsWith('/assignments')
    },
    { 
      label: 'Journals', 
      icon: BookOpen, 
      path: '/journals',
      active: location.pathname.startsWith('/journals')
    },
    { 
      label: 'Growth Plan', 
      icon: RouteIcon, 
      path: '/growth-plan',
      active: location.pathname.startsWith('/growth-plan')
    },
    { 
      label: 'Forum', 
      icon: MessageSquare, 
      path: '/forum',
      active: location.pathname.startsWith('/forum')
    },
    { 
      label: 'Messages', 
      icon: Mail, 
      path: '/messages',
      active: location.pathname.startsWith('/messages'),
      badge: unreadCount,
    },
    { 
      label: 'Meetings', 
      icon: Video, 
      path: '/meetings',
      active: location.pathname.startsWith('/meetings')
    },
    ...(user?.role === 'teacher' ? [{ 
      label: 'Versions', 
      icon: History, 
      path: '/versions',
      active: location.pathname.startsWith('/versions')
    }] : []),
    { 
      label: 'Notifications', 
      icon: Bell, 
      path: '/notifications',
      active: location.pathname === '/notifications',
      badge: notificationCount,
      badgeColor: 'bg-red-500 hover:bg-red-500',
    },
    {
      label: 'Planning Center',
      icon: Link2,
      path: '/planning-center',
      active: location.pathname.startsWith('/planning-center'),
      superAdminOnly: true,
    },
  ].filter((item) => !item.superAdminOnly || user?.is_super_admin);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
      toast.success("Logged out successfully");
      navigate('/');
    } catch (error) {
      console.error("Logout error:", error);
      navigate('/');
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?';
  };

  const SidebarContent = ({ isMobile = false }) => (
    <TooltipProvider delayDuration={200}>
      <div className="flex flex-col h-full">
        {/* Logo / App Switcher */}
        <div className={`shrink-0 h-16 flex items-center ${collapsed && !isMobile ? 'justify-center px-2' : 'px-2'}`}>
          <SidebarBrand
            brandLabel="Discipleship"
            collapsed={collapsed && !isMobile}
            alwaysExpanded={isMobile}
            onClick={collapsed && !isMobile ? () => navigate(user?.role === 'teacher' ? '/teacher' : '/student') : undefined}
          />
        </div>

        <div className={collapsed && !isMobile ? 'px-2' : 'px-1'}>
          <SwitchAppList user={user} active="discipleship" collapsed={collapsed && !isMobile} alwaysExpanded={isMobile} />
        </div>

        <Separator className="mx-3" />

        {(!collapsed || isMobile) && (
          <p className="px-4 mt-3 mb-1 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
            Current App
          </p>
        )}

        {/* Nav Items */}
        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5" data-testid="sidebar-nav">
          {navItems.map((item) => {
            const btn = (
              <button
                key={item.path}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                onClick={() => {
                  navigate(item.path);
                  if (isMobile) setMobileMenuOpen(false);
                }}
                className={`
                  group relative flex items-center w-full rounded-lg transition-all duration-200
                  ${collapsed && !isMobile ? 'justify-center px-2 py-2.5' : 'px-3 py-2.5 gap-3'}
                  ${item.active
                    ? 'bg-amber-50 text-amber-700 font-semibold'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                  }
                `}
              >
                <item.icon className={`shrink-0 ${item.active ? 'text-amber-600' : 'text-slate-400 group-hover:text-slate-600'} ${collapsed && !isMobile ? 'w-5 h-5' : 'w-[18px] h-[18px]'}`} />
                {(!collapsed || isMobile) && (
                  <span className="text-sm truncate">{item.label}</span>
                )}
                {item.badge > 0 && (
                  <Badge className={`${collapsed && !isMobile ? 'absolute -top-1 -right-1 h-4 min-w-4 text-[10px]' : 'ml-auto h-5 min-w-5 text-xs'} flex items-center justify-center p-0 ${item.badgeColor || 'bg-amber-500 hover:bg-amber-500'} text-white`}>
                    {item.badge > 99 ? '99+' : item.badge}
                  </Badge>
                )}
                {item.active && (
                  <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 bg-amber-500 rounded-r-full" />
                )}
              </button>
            );

            if (collapsed && !isMobile) {
              return (
                <Tooltip key={item.path}>
                  <TooltipTrigger asChild>{btn}</TooltipTrigger>
                  <TooltipContent side="right" className="text-xs font-medium">
                    {item.label}
                    {item.badge > 0 && ` (${item.badge})`}
                  </TooltipContent>
                </Tooltip>
              );
            }
            return <div key={item.path}>{btn}</div>;
          })}
        </nav>

        <Separator className="mx-3" />

        {/* User Section */}
        <div className={`shrink-0 p-2 ${collapsed && !isMobile ? 'flex flex-col items-center gap-2' : ''}`}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="user-menu-trigger"
                className={`
                  w-full flex items-center rounded-lg p-2 transition-colors hover:bg-slate-100
                  ${collapsed && !isMobile ? 'justify-center' : 'gap-3'}
                `}
              >
                <Avatar className={`${collapsed && !isMobile ? 'h-9 w-9' : 'h-8 w-8'} shrink-0`}>
                  <AvatarImage src={user?.picture} alt={user?.name} />
                  <AvatarFallback className="bg-amber-100 text-amber-700 text-xs">
                    {getInitials(user?.name)}
                  </AvatarFallback>
                </Avatar>
                {(!collapsed || isMobile) && (
                  <div className="min-w-0 text-left">
                    <div className="flex items-center gap-1">
                      <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
                      {user?.is_super_admin && <Shield className="w-3 h-3 text-purple-600 shrink-0" />}
                    </div>
                    <p className="text-xs text-slate-500 capitalize truncate">
                      {user?.is_super_admin ? 'Super Admin' : user?.role === 'teacher' ? 'Admin' : 'Student'}
                    </p>
                  </div>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56" side={collapsed && !isMobile ? "right" : "top"} align="start" forceMount>
              <DropdownMenuItem onClick={() => navigate('/profile')}>
                <User className="w-4 h-4 mr-2" />
                My Profile
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="sidebar-versions-link" onClick={() => navigate('/versions')}>
                <History className="w-4 h-4 mr-2" />
                Versions
              </DropdownMenuItem>
              {user?.role === "teacher" && (
                <DropdownMenuItem onClick={() => navigate("/role-select")}>
                  <GraduationCap className="w-4 h-4 mr-2" />
                  Switch View
                </DropdownMenuItem>
              )}
              {user?.is_super_admin && (
                <DropdownMenuItem onClick={() => navigate('/teachers')}>
                  <Users className="w-4 h-4 mr-2" />
                  Manage Teachers
                </DropdownMenuItem>
              )}
              {user?.role === 'teacher' && (
                <DropdownMenuItem onClick={() => navigate('/app-settings')}>
                  <Settings className="w-4 h-4 mr-2" />
                  App Settings
                </DropdownMenuItem>
              )}
              {user?.is_super_admin && (
                <DropdownMenuItem onClick={() => navigate('/sms-admin')}>
                  <Smartphone className="w-4 h-4 mr-2" />
                  SMS Admin
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600 focus:text-red-600">
                <LogOut className="w-4 h-4 mr-2" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Collapse toggle - desktop only */}
          {!isMobile && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  data-testid="collapse-sidebar-btn"
                  onClick={() => setCollapsed(!collapsed)}
                  className="w-full flex items-center justify-center rounded-lg p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                >
                  {collapsed ? <PanelLeft className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" className="text-xs">
                {collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>
    </TooltipProvider>
  );

  if (!canRenderPage) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div data-testid="intensive-access-restricted" className="max-w-md w-full text-center bg-white border border-slate-200 rounded-2xl p-10 shadow-sm">
          <div className="w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-5">
            <Lock className="w-6 h-6 text-amber-600" />
          </div>
          <h1 className="font-serif text-2xl font-bold text-slate-900 mb-2">Discipleship Intensive is locked</h1>
          <p className="text-sm text-slate-500 mb-6">
            You don't currently have access to the Discipleship Intensive class. Reach out to an admin if
            you believe this is a mistake.
          </p>
          <Button data-testid="intensive-access-go-home-btn" onClick={() => navigate('/home')} className="bg-amber-600 hover:bg-amber-700">
            Back to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Desktop Sidebar */}
      <aside
        data-testid="desktop-sidebar"
        className={`
          hidden md:flex flex-col fixed top-0 left-0 h-screen bg-white border-r border-slate-200/80 z-40
          transition-all duration-300 ease-in-out
          ${collapsed ? 'w-[68px]' : 'w-60'}
        `}
      >
        {SidebarContent({})}
      </aside>

      {/* Mobile top bar + sidebar sheet */}
      <header className="md:hidden fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-xl border-b border-slate-100 h-14">
        <div className="flex items-center justify-between h-full px-4">
          <div
            className="flex items-center gap-2.5 cursor-pointer"
            onClick={() => navigate(user?.role === 'teacher' ? '/teacher' : '/student')}
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_disciple-hub-3/artifacts/wqhxldc5_Live%20Church%20Logo%20Print.png"
              alt="Live Church"
              className="h-7 w-auto"
            />
            <span className="font-serif font-bold text-sm text-slate-900">
              Discipleship
            </span>
          </div>

          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button data-testid="mobile-menu-trigger" variant="ghost" size="icon" className="h-9 w-9 relative">
                <Menu className="w-5 h-5" />
                {(unreadCount + notificationCount) > 0 && (
                  <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-amber-500 rounded-full" />
                )}
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-0">
              {SidebarContent({ isMobile: true })}
            </SheetContent>
          </Sheet>
        </div>
      </header>

      {/* Main Content */}
      <div className={`flex-1 transition-all duration-300 ${collapsed ? 'md:ml-[68px]' : 'md:ml-60'}`}>
        <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 mt-14 md:mt-0">
          {children}
        </main>
        <footer className="border-t border-slate-100 py-6 mt-auto">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="text-center text-sm text-slate-500">
              Discipleship Intensive — Growing disciples, one lesson at a time.
            </p>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default Layout;
